/**
 * Create vs edit initial values for service agreement forms.
 *
 * Defaults (today / notice_months 3) apply only when creating a new record.
 * On edit, API values — including null, 0, and false — must survive roundtrips.
 * Missing keys on a partial payload must not invent today/3 (that overwrites DB).
 */

const ROUNDTRIP_PRESERVE_KEYS = [
  "agreement_start_date",
  "notice_months",
  "order_date",
  "period_end_date",
  "next_service_date",
  "duration_years",
  "auto_renew",
  "discount_percent",
  "discount_on_parts",
  "discount_on_services",
  "hourly_rate",
  "callout_fee",
  "parts_markup_percent",
  "subcontractor_markup_percent",
  "offer_price",
  "description",
  "agreement_text",
  "conditions",
];

export function isNewServiceAgreement(item) {
  return item == null || item.id == null || item.id === "";
}

export function todayIsoDate(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

function hasOwn(obj, key) {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Merge a (possibly partial) API item onto the previous one so omitted keys
 * do not wipe known values when Formik enableReinitialize runs.
 */
export function mergeAgreementFormItem(previous, next) {
  if (!next) return previous ?? next;
  if (!previous?.id || previous.id !== next.id) return next;

  const merged = { ...previous, ...next };
  for (const key of ROUNDTRIP_PRESERVE_KEYS) {
    if (!hasOwn(next, key) && hasOwn(previous, key)) {
      merged[key] = previous[key];
    }
  }
  return merged;
}

/**
 * Create defaults only for new records. Edit preserves API values (incl. 0/null).
 * @param {object|null|undefined} item
 * @param {{ today?: string }} [options]
 */
export function buildServiceAgreementFormInitialValues(item, options = {}) {
  const today = options.today ?? todayIsoDate();
  const isCreate = isNewServiceAgreement(item);

  const agreementStartDate = isCreate
    ? item?.agreement_start_date ?? today
    : hasOwn(item, "agreement_start_date")
      ? item.agreement_start_date ?? ""
      : "";

  const noticeMonths = isCreate
    ? item?.notice_months ?? 3
    : hasOwn(item, "notice_months")
      ? item.notice_months ?? ""
      : "";

  const orderDate = isCreate
    ? item?.order_date ?? today
    : hasOwn(item, "order_date")
      ? item.order_date ?? ""
      : "";

  return {
    id: item?.id,
    order_date: orderDate,
    agreement_number: item?.agreement_number,
    customer_id: item?.customer?.id,
    invoice_customer_id: item?.invoice_customer?.id,
    project_id: item?.project?.id,
    agreement_type_id: item?.type?.id,
    interval_id: item?.interval?.id,
    invoice_customer: item?.invoice_customer,
    agreement_start_date: agreementStartDate,
    system_id: item?.system?.id,
    description: item?.description,
    agreement_text: item?.agreement_text,
    conditions: item?.conditions,
    invoice_to_project_customer: item?.invoice_to_project_customer,
    delivery_address_id: item?.delivery_address?.id,
    contact_id: item?.contact?.id,
    is_framework: item?.is_framework === true,
    discount_percent: item?.discount_percent ?? "",
    discount_on_parts: item?.discount_on_parts ?? "",
    discount_on_services: item?.discount_on_services ?? "",
    duration_years: item?.duration_years ?? "",
    notice_months: noticeMonths,
    auto_renew: item?.auto_renew !== false,
    vat_included: item?.vat_included === true,
    period_end_date: item?.period_end_date ?? "",
    hourly_rate: item?.hourly_rate ?? "",
    callout_fee: item?.callout_fee ?? "",
    parts_markup_percent: item?.parts_markup_percent ?? "",
    subcontractor_markup_percent: item?.subcontractor_markup_percent ?? "",
    offer_price: item?.offer_price ?? "",
    discount_lines: Array.isArray(item?.discount_lines) ? item.discount_lines : [],
  };
}

/**
 * Build update/create payload. On edit, omit roundtrip fields that were absent
 * from the source item and left empty — avoids wiping stored DB values.
 */
export function buildServiceAgreementSavePayload(values, sourceItem) {
  const payload = { ...values };
  if (isNewServiceAgreement(values) || isNewServiceAgreement(sourceItem)) {
    return payload;
  }

  const omitIfAbsentAndEmpty = (key) => {
    if (hasOwn(sourceItem, key)) return;
    const v = payload[key];
    if (v === "" || v === undefined || v === null) {
      delete payload[key];
    }
  };

  omitIfAbsentAndEmpty("agreement_start_date");
  omitIfAbsentAndEmpty("notice_months");
  omitIfAbsentAndEmpty("order_date");

  return payload;
}
